import { initializeApp } from "firebase/app";
import { initializeFirestore, doc, setDoc, getDoc, serverTimestamp } from "firebase/firestore";
import { getStorage } from "firebase/storage";
import { DRXError } from "./drx-error";
import { 
  getAuth, 
  GoogleAuthProvider, 
  signInWithPopup, 
  signInWithEmailAndPassword,
  createUserWithEmailAndPassword, 
  signOut,
  User as FirebaseUser,
  UserCredential,
  onAuthStateChanged,
  updateProfile,
  sendEmailVerification,
  sendPasswordResetEmail,
  applyActionCode,
  confirmPasswordReset,
  verifyPasswordResetCode
} from "firebase/auth";
import { getAnalytics, isSupported, Analytics } from "firebase/analytics";
import { getFunctions, httpsCallable } from "firebase/functions";

// Firebase configuration from environment variables
const firebaseConfig = {
  apiKey: import.meta.env.VITE_FIREBASE_API_KEY,
  authDomain: import.meta.env.VITE_FIREBASE_AUTH_DOMAIN,
  projectId: import.meta.env.VITE_FIREBASE_PROJECT_ID,
  storageBucket: import.meta.env.VITE_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: import.meta.env.VITE_FIREBASE_MESSAGING_SENDER_ID,
  appId: import.meta.env.VITE_FIREBASE_APP_ID,
  measurementId: import.meta.env.VITE_FIREBASE_MEASUREMENT_ID
};

// Web Client ID for OAuth
export const webClientId = import.meta.env.VITE_GOOGLE_WEB_CLIENT_ID;

// Initialize Firebase
const app = initializeApp(firebaseConfig);
const auth = getAuth(app);

// Staging talks to its OWN database and bucket.
//
// Until now the client SDK was hard-bound to (default), so every page on
// staging read and wrote PRODUCTION data no matter what -- editing a
// storefront on staging changed the real one, and panels showed prod numbers
// next to callable-sourced staging numbers on the same screen. Pointing the
// client at the staging database is only safe because that database is now a
// full copy of prod (scripts/copy-prod-to-staging.js); against the empty
// database it previously was, this would have blanked the entire staging UI.
//
// Auth is deliberately NOT split: Firebase Auth has one user pool per
// project, so the same credentials must keep working on both.
const isStaging = typeof __APP_VERSION__ === 'string' && __APP_VERSION__.endsWith('-staging');
const STAGING_DATABASE_ID = 'staging';
const STAGING_STORAGE_BUCKET = 'gs://dancerealmx-b4a0a-staging';

// Long polling, not the streaming connection: in Safari the stream's
// requests fail ("access control checks") and reads waiting on it never
// return, so the Instructor panel's My Courses sat on "Loading your
// courses..." for every Safari user (2026-10-31).
const firestoreSettings = { experimentalForceLongPolling: true };
const db = isStaging
  ? initializeFirestore(app, firestoreSettings, STAGING_DATABASE_ID)
  : initializeFirestore(app, firestoreSettings);
const storage = isStaging ? getStorage(app, STAGING_STORAGE_BUCKET) : getStorage(app);
const functions = getFunctions(app);
let analytics: Analytics | null = null;
void isSupported()
  .then((supported) => {
    if (supported) {
      analytics = getAnalytics(app);
    }
  })
  .catch((error) => {
    console.warn("Firebase analytics unavailable in this environment:", error);
  });
const googleProvider = new GoogleAuthProvider();

// Configure Google provider
googleProvider.addScope('profile');
googleProvider.addScope('email');
googleProvider.setCustomParameters({
  prompt: 'select_account',
  access_type: 'online'
});

// Helper function to ensure user profile exists in Firestore
export async function ensureUserProfileExists(user: FirebaseUser): Promise<void> {
  try {
    const userDocRef = doc(db, 'users', user.uid);
    const preferencesRef = doc(db, 'users', user.uid, 'preferences', 'default');
    const userDoc = await getDoc(userDocRef);
    
    if (!userDoc.exists()) {
      // Create user profile document
      const userData = {
        email: user.email || '',
        // Usernames have no spaces; registration overwrites this with the
        // one the person chose.
        username: (user.email?.split('@')[0] || user.uid.slice(0, 12)).toLowerCase().replace(/[^a-z0-9_.-]/g, ''),
        firstName: user.displayName?.split(' ')[0] || '',
        lastName: user.displayName?.split(' ').slice(1).join(' ') || '',
        roles: ['USER'],
        isActive: true,
        emailVerified: user.emailVerified,
        phoneVerified: false,
        createdAt: new Date(),
        updatedAt: new Date(),
        lastLoginAt: new Date(),
        profileCompleted: false,
      };
      
      await setDoc(userDocRef, userData);
      await setDoc(preferencesRef, {
        general: {
          notifications: true,
          marketingEmails: true,
          theme: 'light',
        },
        migratedFromUserDoc: false,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      }, { merge: true });
      console.log('Created user profile for:', user.uid);
    } else {
      // Update last login. If the profile was created before the display
      // name existed (auth listener racing signup), fill the names in now.
      const existing = userDoc.data() as { firstName?: string; lastName?: string };
      const fillNames =
        !String(existing.firstName ?? '').trim() && user.displayName?.trim()
          ? {
              firstName: user.displayName.trim().split(/\s+/)[0],
              lastName: user.displayName.trim().split(/\s+/).slice(1).join(' '),
            }
          : {};
      await setDoc(userDocRef, {
        lastLoginAt: new Date(),
        updatedAt: new Date(),
        ...fillNames,
      }, { merge: true });
    }
  } catch (error) {
    // Offline (a dropped connection, a laptop waking) is not a fault: the
    // check runs again at the next sign-in. Anything else is worth reporting.
    const e = error as { code?: string; message?: string };
    if (e?.code === 'unavailable' || /client is offline/i.test(String(e?.message ?? ''))) {
      console.warn('Profile check skipped while offline:', e?.message);
    } else {
      console.error('Error ensuring user profile exists:', error);
    }
    // Don't throw - this shouldn't block authentication
  }
}

/**
 * Auth-error codes that represent normal user-input mistakes (wrong password,
 * email already in use, popup closed, etc.). The caller maps these into
 * friendly UI messages via `mapFirebaseError`. Logging them via console.error
 * causes the consoleErrorBridge to forward them to Sentry as alerts — that's
 * noise. Use console.warn for these so the error is still visible in the
 * browser console without paging us in production.
 */
const HANDLED_AUTH_CODES = new Set([
  'auth/user-not-found',
  'auth/wrong-password',
  'auth/invalid-credential',
  'auth/email-already-in-use',
  'auth/invalid-email',
  'auth/weak-password',
  'auth/user-disabled',
  'auth/too-many-requests',
  'auth/operation-not-allowed',
  'auth/popup-closed-by-user',
  'auth/popup-blocked',
  'auth/cancelled-popup-request',
  'auth/account-exists-with-different-credential',
  'auth/network-request-failed',
  // Outages on Google's side; the sign-in form says to try again shortly.
  'auth/the-service-is-currently-unavailable.',
  'auth/the-service-is-currently-unavailable',
  'auth/quota-exceeded',
  'auth/internal-error',
]);

function logAuthError(prefix: string, error: unknown): void {
  const code = (error as { code?: string } | null)?.code ?? '';
  if (HANDLED_AUTH_CODES.has(code)) {
    console.warn(`${prefix} (handled):`, code);
  } else {
    console.error(prefix, error);
  }
}

// Auth functions
export async function signInWithGoogle(): Promise<UserCredential> {
  try {
    const result = await signInWithPopup(auth, googleProvider);

    // Create user profile in Firestore if it doesn't exist
    if (result.user) {
      await ensureUserProfileExists(result.user);
    }

    return result;
  } catch (error) {
    logAuthError("Error signing in with Google:", error);
    throw error;
  }
}

export async function signInWithEmail(email: string, password: string): Promise<UserCredential> {
  try {
    const result = await signInWithEmailAndPassword(auth, email, password);
    return result;
  } catch (error) {
    logAuthError("Error signing in with email:", error);
    throw error;
  }
}

export async function signUpWithEmail(email: string, password: string, displayName?: string): Promise<UserCredential> {
  try {
    const result = await createUserWithEmailAndPassword(auth, email, password);
    
    // Update profile with display name if provided
    if (displayName && result.user) {
      await updateProfile(result.user, { displayName });
    }
    
    // The profile comes first. It used to be created after the verification
    // email, so when Postmark refused a send (403, 2026-10-05) and the Firebase
    // fallback threw too, signup aborted with an Auth account but no
    // users/{uid} -- and every profile save after that was denied.
    if (result.user) {
      await ensureUserProfileExists(result.user);
    }

    // Verification email: never allowed to fail the signup. The user can
    // resend it from the verify-email banner.
    if (result.user && !result.user.emailVerified) {
      try {
        const sendVerification = httpsCallable(functions, 'sendEmailVerification');
        await sendVerification({
          email: result.user.email,
          userId: result.user.uid
        });
      } catch (emailError) {
        console.warn('Custom verification email failed; trying the Firebase fallback:', emailError);
        try {
          await sendEmailVerification(result.user);
        } catch (fallbackError) {
          console.warn('Fallback verification email failed too:', fallbackError);
        }
      }
    }
    
    return result;
  } catch (error) {
    logAuthError("Error signing up with email:", error);
    throw error;
  }
}

export async function signOutUser(): Promise<void> {
  try {
    await signOut(auth);
  } catch (error) {
    logAuthError("Error signing out:", error);
    throw error;
  }
}

export function onAuthStateChange(callback: (user: FirebaseUser | null) => void) {
  return onAuthStateChanged(auth, callback);
}

// Get current user
export function getCurrentUser(): FirebaseUser | null {
  return auth.currentUser;
}

// Email verification
export async function sendVerificationEmail(user?: FirebaseUser | null): Promise<void> {
  const currentUser = user || auth.currentUser;
  if (!currentUser) {
    throw new Error('No user logged in');
  }
  
  if (currentUser.emailVerified) {
    throw new Error('Email already verified');
  }
  
  await sendEmailVerification(currentUser);
}

// Password reset
export async function requestPasswordReset(email: string): Promise<void> {
  await sendPasswordResetEmail(auth, email);
}

// Export raw Firebase functions for advanced usage
export { sendPasswordResetEmail } from "firebase/auth";

// Verify password reset code
export async function verifyResetCode(code: string): Promise<string> {
  return await verifyPasswordResetCode(auth, code);
}

// Confirm password reset
export async function confirmReset(code: string, newPassword: string): Promise<void> {
  await confirmPasswordReset(auth, code, newPassword);
}

// Apply action code (for email verification)
export async function applyVerificationCode(code: string): Promise<void> {
  await applyActionCode(auth, code);
}

// Get Firebase token
export async function getFirebaseToken(): Promise<string | null> {
  const user = getCurrentUser();
  if (!user) return null;
  
  try {
    const token = await user.getIdToken();
    return token;
  } catch (error) {
    console.error("Error getting Firebase token:", error);
    return null;
  }
}

// Export callable functions
export const uploadProfileImageCallable = httpsCallable(functions, 'uploadProfileImage');
export const uploadCourseImageCallable = httpsCallable(functions, 'uploadCourseImage');
export const uploadLessonVideoCallable = httpsCallable(functions, 'uploadLessonVideo');
// Staging builds (__APP_VERSION__ carries a "-staging" suffix, see
// vite.config.ts) call the *Test Cloud Functions instead -- those run on
// STRIPE_TEST_SECRET_KEY (sk_test_...) and write to a separate Firestore
// collection, so staging can walk through Connect onboarding with Stripe's
// documented test SSNs/bank numbers without ever touching the live key
// real sellers' payouts depend on. Prod is unaffected either way.
const isStagingBuild = typeof __APP_VERSION__ === 'string' && __APP_VERSION__.endsWith('-staging');
export const createStripeAccountCallable = httpsCallable(
  functions,
  isStagingBuild ? 'createStripeAccountTest' : 'createStripeAccount'
);
export const getStripeAccountStatusCallable = httpsCallable(
  functions,
  isStagingBuild ? 'getStripeAccountStatusTest' : 'getStripeAccountStatus'
);
export const refreshStripeOnboardingCallable = httpsCallable<{ returnPath?: string } | void, { url?: string }>(
  functions,
  isStagingBuild ? 'refreshStripeOnboardingTest' : 'refreshStripeOnboarding'
);
// Express sellers can only change their payout bank account from Stripe's own
// Express dashboard -- the platform can't edit external accounts over the API.
export const stripeDashboardLinkCallable = httpsCallable<void, { url: string }>(
  functions,
  isStagingBuild ? 'stripeDashboardLinkTest' : 'stripeDashboardLink'
);

// Payouts don't need the -Test name swap above: these two resolve live vs
// test server-side from the request origin, so there's a single name for
// both environments. See functions/src/stripe/payout.ts.
export const getPayoutSummaryCallable = httpsCallable(functions, 'getPayoutSummary');
// Sellers do not withdraw on demand -- payouts run on the 15th, or an admin
// triggers one. Admin-only, so it is never called from seller-facing screens.
export const adminPayoutSellerCallable = httpsCallable(functions, 'adminPayoutSeller');
export const listPayableSellersCallable = httpsCallable(functions, 'listPayableSellers');
export const getSellerSalesCallable = httpsCallable(functions, 'getSellerSales');
export const getProfessionalJobsSummaryCallable = httpsCallable(functions, 'getProfessionalJobsSummary');
export const changePasswordCallable = httpsCallable(functions, 'changePassword');
// Server-side nested delete of a course / module / lesson and its files.
const deleteCourseContentFn = httpsCallable<
  { scope: 'course' | 'module' | 'lesson' | 'quiz'; id: string; force?: boolean },
  { deleted: Record<string, number>; filesDeleted: number; filesFailed: string[] }
>(functions, 'deleteCourseContent');
/**
 * Refusals ("1 student is enrolled -- unpublish it instead") are the reason
 * the instructor needs to see; carry the server's message as userMessage so
 * it survives the repositories' error wrapping instead of becoming
 * "Failed to delete course <id>".
 */
export async function deleteCourseContentCallable(data: { scope: 'course' | 'module' | 'lesson' | 'quiz'; id: string; force?: boolean }) {
  try {
    return await deleteCourseContentFn(data);
  } catch (error) {
    throw new DRXError({
      code: 'COURSE_CONTENT_DELETE_FAILED',
      devMessage: `deleteCourseContent ${data.scope} ${data.id} failed`,
      userMessage: error instanceof Error && error.message ? error.message : 'Could not delete. Please try again.',
      cause: error,
      level: 'warning',
    });
  }
}
export const sendCertificateEmailCallable = httpsCallable(functions, 'sendCertificateEmail');
export const inviteStudentsCallable = httpsCallable(functions, 'inviteStudents');

// File Upload Functions
export const getSignedUploadUrlCallable = httpsCallable(functions, 'getSignedUploadUrl');

// Payment and Enrollment Functions
export const createCheckoutSessionCallable = httpsCallable(functions, 'createCheckoutSession');

// Admin Functions
export const createSellerCurriculumCallable = httpsCallable(functions, 'createSellerCurriculum');
export const runDeploymentTestsCallable = httpsCallable(functions, 'runDeploymentTests');
export const getDeploymentResultsCallable = httpsCallable(functions, 'getDeploymentResults');
export const adminUploadCallable = httpsCallable(functions, 'adminUpload');
export const adminPayoutCallable = httpsCallable(functions, 'adminPayout');
export const createSellerCallable = httpsCallable(functions, 'createSeller');

// Documentation Functions
export const getDocumentationCallable = httpsCallable(functions, 'getDocumentation');
export const generateDocumentationPDFCallable = httpsCallable(functions, 'generateDocumentationPDF');

// ETL Functions
export const getETLPipelinesCallable = httpsCallable(functions, 'getETLPipelines');
export const createETLPipelineCallable = httpsCallable(functions, 'createETLPipeline');
export const updateETLPipelineCallable = httpsCallable(functions, 'updateETLPipeline');
export const runETLPipelineCallable = httpsCallable(functions, 'runETLPipeline');

// Panel Stats Tracking Functions
export const recordProfileViewCallable = httpsCallable(functions, 'recordProfileView');
export const recordStoreViewCallable = httpsCallable(functions, 'recordStoreView');
export const recordResourceDownloadCallable = httpsCallable(functions, 'recordResourceDownload');

export { auth, db, storage, functions, googleProvider, analytics, app };
